import type { ApiError } from '../apiClient'

/**
 * Generic response wrapper interface
 */
export interface WrappedResponse<T> {
  data?: T
  result?: T
  results?: T
  success?: boolean
  message?: string
}

/**
 * Extract array from various API response formats
 * @param responseData - The response data from API
 * @returns Extracted array or empty array
 */
export function extractArrayFromResponse<T>(
  responseData: unknown
): T[] {
  // Handle direct array response
  if (Array.isArray(responseData)) {
    return responseData
  }

  // Handle wrapped responses
  if (responseData && typeof responseData === 'object') {
    const wrapped = responseData as WrappedResponse<T>

    // Check for data property
    if ('data' in wrapped && Array.isArray(wrapped.data)) {
      return wrapped.data
    }

    // Check for result property
    if ('result' in wrapped && Array.isArray(wrapped.result)) {
      return wrapped.result
    }

    // Check for results property
    if ('results' in wrapped && Array.isArray(wrapped.results)) {
      return wrapped.results
    }

    // Some reference endpoints return { states: [...] } (matches Angular /states usage)
    if ('states' in wrapped && Array.isArray((wrapped as { states?: unknown }).states)) {
      return (wrapped as { states: T[] }).states
    }

    // Check for error response
    if ('success' in wrapped && wrapped.success === false) {
      throw {
        message: wrapped.message || 'API request failed',
        status: 400,
      } as ApiError
    }
  }

  return []
}

/**
 * Check if response indicates an error
 * @param responseData - The response data to check
 * @returns True if response indicates an error
 */
export function isErrorResponse(
  responseData: unknown
): responseData is WrappedResponse<unknown> & { success: false } {
  return (
    typeof responseData === 'object' &&
    responseData !== null &&
    'success' in responseData &&
    (responseData as WrappedResponse<unknown>).success === false
  )
}

